import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import { buildTankAdminUpsertOperation } from "../operations/tankAdministrationOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface HallTankAdministrationPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface TankAdminRow {
  tankId: string;
  tankCode: string;
  tankFamily: string;
  tankNumber: number;
  notes?: string;
  isActive: boolean;
  tankDomainVersion: number;
  occupantCount: number;
  occupantKinds: string[];
  waterMode: string;
  circuitId?: string;
  circuitCode?: string;
  circuitName?: string;
  independentReason?: string;
  waterDomainVersion: number;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) return record(value[0]);
  if (value && typeof value === "object") return record(value);
  return undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function occupancyLabel(kinds: string[], count: number): string {
  if (count === 0) return "خالی";
  const labels = kinds.map((kind) => {
    if (kind === "pair") return "جفت";
    if (kind === "breeder") return "مولد";
    if (kind === "stock_group") return "گله";
    return kind;
  });
  return `${count} اشغال · ${labels.join("، ")}`;
}

function waterLabel(tank: TankAdminRow): string {
  if (tank.waterMode === "connected") {
    return `متصل · ${tank.circuitCode ?? "مدار"}${tank.circuitName ? ` · ${tank.circuitName}` : ""}`;
  }
  if (tank.waterMode === "independent") {
    return `مستقل${tank.independentReason ? ` · ${tank.independentReason}` : ""}`;
  }
  return "آب پیکربندی نشده";
}

function syncMessage(status: string): string {
  if (status === "synced") return "تغییر تانک با سرور همگام شد.";
  if (status === "pending") return "تغییر در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict")
    return "ثبت با وضعیت فعلی تانک تعارض دارد؛ داده را تازه‌سازی کنید.";
  return "تغییر ثبت شد.";
}

async function loadTankAdministration(farmId: string): Promise<{
  canManage: boolean;
  tanks: TankAdminRow[];
}> {
  const [permissionResult, tankResult] = await Promise.all([
    supabase.rpc("permission_decide", {
      target_farm_id: farmId,
      requested_permission: "settings.manage",
    }),
    supabase.rpc("tank_admin_list_v1", {
      target_farm_id: farmId,
    }),
  ]);

  if (permissionResult.error) throw permissionResult.error;
  if (tankResult.error) throw tankResult.error;

  const permission = firstRecord(permissionResult.data);
  const tanks = rows(tankResult.data).map((item) => {
    const notes = optionalString(item.notes);
    const circuitId = optionalString(item.circuit_id);
    const circuitCode = optionalString(item.circuit_code);
    const circuitName = optionalString(item.circuit_name);
    const independentReason = optionalString(item.independent_reason);

    return {
      tankId: String(item.tank_id),
      tankCode: String(item.tank_code ?? ""),
      tankFamily: String(item.tank_family ?? ""),
      tankNumber: Number(item.tank_number ?? 0),
      ...(notes ? { notes } : {}),
      isActive: item.is_active === true,
      tankDomainVersion: Number(item.tank_domain_version ?? 0),
      occupantCount: Number(item.occupant_count ?? 0),
      occupantKinds: stringArray(item.occupant_kinds),
      waterMode: String(item.water_mode ?? "unconfigured"),
      ...(circuitId ? { circuitId } : {}),
      ...(circuitCode ? { circuitCode } : {}),
      ...(circuitName ? { circuitName } : {}),
      ...(independentReason ? { independentReason } : {}),
      waterDomainVersion: Number(item.water_domain_version ?? 0),
    } satisfies TankAdminRow;
  });

  return {
    canManage: permission?.allowed === true,
    tanks,
  };
}

export function HallTankAdministrationPanel({
  farmId,
  userId,
  onSyncSummary,
}: HallTankAdministrationPanelProps) {
  const [tanks, setTanks] = useState<TankAdminRow[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);

  const [editingTankId, setEditingTankId] = useState<string>();
  const [tankCode, setTankCode] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const next = await loadTankAdministration(farmId);
      setTanks(next.tanks);
      setCanManage(next.canManage);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری تانک‌ها انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeCount = useMemo(() => tanks.filter((tank) => tank.isActive).length, [tanks]);

  function clearEditor() {
    setEditingTankId(undefined);
    setTankCode("");
    setNotes("");
    setIsActive(true);
  }

  function editTank(tank: TankAdminRow) {
    setEditingTankId(tank.tankId);
    setTankCode(tank.tankCode);
    setNotes(tank.notes ?? "");
    setIsActive(tank.isActive);
    setMessage(undefined);
    setError(undefined);
  }

  function prepareSave() {
    setMessage(undefined);
    setError(undefined);

    if (!canManage) {
      setError("دسترسی مدیریت تنظیمات برای این حساب فعال نیست.");
      return;
    }

    const existing = editingTankId
      ? tanks.find((tank) => tank.tankId === editingTankId)
      : undefined;
    const tankId = existing?.tankId ?? crypto.randomUUID();

    if (existing?.isActive && !isActive) {
      if (existing.occupantCount > 0) {
        setError("تانک اشغال است؛ قبل از غیرفعال‌سازی باید موجودی از تانک خارج شود.");
        return;
      }
      if (existing.waterMode === "connected") {
        setError("تانک به مدار آب متصل است؛ قبل از غیرفعال‌سازی باید از مدار جدا شود.");
        return;
      }
    }

    try {
      const operation = buildTankAdminUpsertOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          tankId,
          expectedVersion: existing?.tankDomainVersion ?? 0,
          tankCode,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          isActive,
        },
      );

      setPending(operation);
      setConfirmationParts([
        existing ? "ویرایش تانک" : "ایجاد تانک",
        `کد: ${operation.payload.tankCode as string}`,
        `وضعیت: ${isActive ? "فعال" : "غیرفعال"}`,
        `توضیح: ${notes.trim() || "بدون توضیح"}`,
        `نسخه مورد انتظار: ${existing?.tankDomainVersion ?? 0}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات تانک معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        clearEditor();
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تغییر تانک انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && tanks.length === 0) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری تانک‌ها…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="hall-tank-admin-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4C1</p>
          <h2 id="hall-tank-admin-title">سالن و تانک‌ها</h2>
        </div>
        <span className="count-pill">
          {activeCount} فعال / {tanks.length} کل
        </span>
      </div>

      <p className="section-note">
        کد تانک بعد از ایجاد ثابت می‌ماند. حذف فیزیکی وجود ندارد؛ غیرفعال‌سازی تانک اشغال یا متصل به
        مدار آب در سرور رد می‌شود.
      </p>

      <div className="work-list">
        {tanks.map((tank) => (
          <div className="work-item pending" key={tank.tankId}>
            <span className="work-main">
              <strong>
                {tank.tankCode} · {tank.isActive ? "فعال" : "غیرفعال"}
              </strong>
              <span>
                {occupancyLabel(tank.occupantKinds, tank.occupantCount)} · {waterLabel(tank)} · نسخه
                تانک {tank.tankDomainVersion} · نسخه آب {tank.waterDomainVersion}
              </span>
              {tank.notes ? <span>{tank.notes}</span> : null}
            </span>
            <button type="button" className="text-button" onClick={() => editTank(tank)}>
              ویرایش
            </button>
          </div>
        ))}
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">مدیریت تانک</p>
            <h3>{editingTankId ? "ویرایش تانک" : "تانک جدید"}</h3>
          </div>
          {editingTankId ? (
            <button type="button" className="text-button" onClick={clearEditor}>
              تانک جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            کد تانک
            <input
              value={tankCode}
              disabled={Boolean(editingTankId)}
              onChange={(event) => setTankCode(event.target.value)}
              placeholder="B001 / P001 / G001 / Q001"
            />
          </label>
          <label>
            توضیحات
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              maxLength={1000}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            تانک فعال است
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canManage}
          onClick={prepareSave}
        >
          بررسی و ثبت تانک
        </button>
      </div>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید مدیریت تانک"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
